import { AlertTriangle } from 'lucide-react'
import pluralize from 'pluralize'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'
import { formatCurrency } from '../../../../lib/utils'
import { AutoLiabilityEstimate, useAutoLiabilityEstimate } from '../../../../models/autoLiabilityEstimate'

const LAST_DOT_STORAGE_KEY = 'insuranceEstimator.lastDot'

export function InsuranceEstimator() {
  const navigate = useNavigate()
  const { dotNumber } = useParams<{ dotNumber?: string }>()
  const [input, setInput] = useState(dotNumber ?? '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (dotNumber) {
      setInput(dotNumber)
      return
    }
    const stored = localStorage.getItem(LAST_DOT_STORAGE_KEY)
    if (stored) setInput(stored)
  }, [dotNumber])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = input.trim()
    if (!/^\d+$/.test(trimmed)) {
      setError('DOT number must be numeric')
      return
    }
    setError(null)
    localStorage.setItem(LAST_DOT_STORAGE_KEY, trimmed)
    navigate(`/insure/internal/sales/insurance-estimator/${trimmed}`)
  }

  return (
    <>
      <Helmet>
        <title>Insurance Estimator</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <BetaWarningBanner />

        <div className="flex items-center gap-3">
          <span className="text-2xl font-semibold">Insurance Estimator</span>
          <Badge variant="secondary">Beta</Badge>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-1">
          <label htmlFor="dot" className="text-sm font-medium">
            DOT Number
          </label>
          <div className="flex items-center gap-3">
            <Input
              id="dot"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. 3300318"
              className="w-64"
              autoFocus
            />
            <Button type="submit">Estimate</Button>
          </div>
          {error && <span className="text-destructive text-xs">{error}</span>}
        </form>

        {dotNumber && <EstimateResults dotNumber={dotNumber} />}
      </div>
    </>
  )
}

function BetaWarningBanner() {
  return (
    <div className="border-warning bg-warning/10 flex items-start gap-3 rounded-lg border p-4">
      <AlertTriangle className="text-warning mt-0.5 h-5 w-5 shrink-0" />
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold">Beta — internal use only</span>
        <span className="text-muted-foreground text-sm">
          This estimator uses an unvalidated heuristic and is not a binding quote. Do not share these numbers
          with carriers, brokers, or any party outside Cai Labs.
        </span>
      </div>
    </div>
  )
}

function EstimateResults({ dotNumber }: { dotNumber: string }) {
  const estimateQuery = useAutoLiabilityEstimate(dotNumber)

  if (estimateQuery.isLoading) {
    return (
      <SkeletonGroup className="grid grid-cols-3 gap-6 py-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-20 w-full" />
        ))}
      </SkeletonGroup>
    )
  }

  if (estimateQuery.isError) {
    const detail = estimateQuery.error.response?.data.detail
    const message =
      detail ||
      (estimateQuery.error.response?.status === 404
        ? `No CAB record found for DOT ${dotNumber}.`
        : 'Failed to compute an estimate for this DOT.')
    return <div className="text-muted-foreground py-6 text-sm">{message}</div>
  }

  if (!estimateQuery.data) return null

  return <EstimateContent estimate={estimateQuery.data} />
}

function EstimateContent({ estimate }: { estimate: AutoLiabilityEstimate }) {
  const limitLabel = `$${(estimate.requestedLimit / 1_000_000).toFixed(0)}M limit`
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Auto Liability Estimate</CardTitle>
          <div className="text-muted-foreground text-sm">
            Estimation for {estimate.powerUnits} {pluralize('truck', estimate.powerUnits)} · {limitLabel}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <RangeCell label="Low" total={estimate.totalLow} perUnit={estimate.perUnitLow} />
            <RangeCell label="Midpoint" total={estimate.totalMid} perUnit={estimate.perUnitMid} emphasized />
            <RangeCell label="High" total={estimate.totalHigh} perUnit={estimate.perUnitHigh} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Factor breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-left">
                <th className="pb-2 font-medium">Factor</th>
                <th className="pb-2 font-medium">Multiplier</th>
                <th className="pb-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {estimate.factors.map((f) => (
                <tr key={f.name} className="border-b last:border-b-0">
                  <td className="py-2 capitalize">{f.name.replace(/_/g, ' ')}</td>
                  <td className="py-2">{f.multiplier.toFixed(2)}×</td>
                  <td className="text-muted-foreground py-2">{f.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

function RangeCell({
  label,
  total,
  perUnit,
  emphasized,
}: {
  label: string
  total: number
  perUnit: number
  emphasized?: boolean
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{label}</span>
      <span
        className={
          emphasized
            ? 'text-general-foreground text-2xl font-semibold'
            : 'text-general-foreground text-lg font-semibold'
        }
      >
        {formatCurrency(total)}
      </span>
      <span className="text-muted-foreground text-xs">{formatCurrency(perUnit)} / unit</span>
    </div>
  )
}
